import React from 'react';
import { isEmpty } from 'lodash';

import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import VoiceMessageDetailsPreview from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/VoiceMessageDetailsPreview/VoiceMessageDetailsPreview';
import type { PhoneSurveyConfigurationModel } from '~/server_shared/openapi/data-contracts';

interface PhoneSurveyConfigurationViewProps {
  surveyConfiguration: PhoneSurveyConfigurationModel;
  isLoading: boolean;
  isError: boolean;
}

const PhoneSurveyConfigurationView: React.FC<PhoneSurveyConfigurationViewProps> = ({
  surveyConfiguration,
  isLoading = false,
  isError = false,
}) => {
  if (isLoading || isError) {
    return <SkeletonTable rowsCount={5} columnsCount={1} isError={isError} />;
  }

  return (
    <>
      <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
        Configure Automatic Survey
      </Text>
      <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD} colorVariant={Text.COLOR_VARIANTS.DISABLED}>
        Set survey questions to be asked automatically at the end of every phone call.
      </Text>

      {!isEmpty(surveyConfiguration) && (
        <div className="mt-20">
          {surveyConfiguration.welcome_message_configuration ? (
            <div className="mb-20">
              <VoiceMessageDetailsPreview
                configurationType={surveyConfiguration.welcome_message_configuration.configuration_type}
                messageTextTitle="Welcome by text message"
                messageText={surveyConfiguration.welcome_message_configuration.message_text}
                uploadedTextMessageTitle="Welcome by recording"
                audioUrl={surveyConfiguration.welcome_message_configuration.permanent_audio_file_url}
              />
            </div>
          ) : null}

          {surveyConfiguration.questions && surveyConfiguration.questions.length > 0 ? (
            <div className="mb-20">
              <Text variant={Text.VARIANTS.LG}>Survey Questions</Text>
              {surveyConfiguration.questions.map((question, index) => (
                <div key={index} className="m-12 rounded-lg border-1 border-solid border-slate-300 p-20">
                  <VoiceMessageDetailsPreview
                    configurationType={question?.question_configuration?.configuration_type}
                    messageTextTitle={`Question ${index + 1} by text message`}
                    messageText={question?.question_configuration?.message_text || ''}
                    uploadedTextMessageTitle={`Question ${index + 1} by recording`}
                    audioUrl={question?.question_configuration?.permanent_audio_file_url}
                  />

                  {question?.answer_options && question?.answer_options?.length > 0 ? (
                    <div className="mt-10">
                      <Text variant={Text.VARIANTS.SM}>Answer Options:</Text>
                      <div className="p-12">
                        {question?.answer_options?.map((option, optionIndex) => (
                          <div key={optionIndex} className="ml-10 p-12">
                            <VoiceMessageDetailsPreview
                              configurationType={option?.content_configuration?.configuration_type}
                              messageTextTitle={`Answer Option ${optionIndex + 1}`}
                              messageText={option?.content_configuration?.message_text || ''}
                              uploadedTextMessageTitle={`Answer Option ${optionIndex + 1} by recording`}
                              audioUrl={option?.content_configuration?.permanent_audio_file_url}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {surveyConfiguration.illegal_response_text_configuration ? (
            <div className="mb-20">
              <VoiceMessageDetailsPreview
                configurationType={surveyConfiguration.illegal_response_text_configuration.configuration_type}
                messageTextTitle="Illegal response by text message"
                messageText={surveyConfiguration.illegal_response_text_configuration.message_text}
                uploadedTextMessageTitle="Illegal response by recording"
                audioUrl={surveyConfiguration.illegal_response_text_configuration.permanent_audio_file_url}
              />
            </div>
          ) : null}

          {surveyConfiguration.ending_message_configuration ? (
            <div className="mb-20">
              <VoiceMessageDetailsPreview
                configurationType={surveyConfiguration.ending_message_configuration.configuration_type}
                messageTextTitle="Ending by text message"
                messageText={surveyConfiguration.ending_message_configuration.message_text}
                uploadedTextMessageTitle="Ending by recording"
                audioUrl={surveyConfiguration.ending_message_configuration.permanent_audio_file_url}
              />
            </div>
          ) : null}
        </div>
      )}
    </>
  );
};

export default PhoneSurveyConfigurationView;
